import { useCallback, useEffect, useState } from "react";
import { Mail, MailOpen, MailCheck, Search, Undo2, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "./api";
import { btnGhost, ConfirmButton, inputCls, Spinner, StatusBadge, fmtDateTime } from "./ui";

const STATUS_FILTERS = [
    { id: "", label: "Όλα" },
    { id: "new", label: "Νέο" },
    { id: "read", label: "Διαβάστηκε" },
    { id: "replied", label: "Απαντήθηκε" },
];

export const MessagesPage = () => {
    const [messages, setMessages] = useState(null);
    const [status, setStatus] = useState("");
    const [q, setQ] = useState("");
    const [selected, setSelected] = useState(null);

    const load = useCallback(() => {
        const params = {};
        if (status) params.status = status;
        if (q.trim()) params.q = q.trim();
        api.get("/api/admin/messages", { params })
            .then((r) => setMessages(r.data))
            .catch((e) => {
                toast.error(apiError(e));
                setMessages([]);
            });
    }, [status, q]);

    useEffect(() => {
        const t = setTimeout(load, q ? 300 : 0);
        return () => clearTimeout(t);
    }, [load, q]);

    const setMsgStatus = async (m, next) => {
        try {
            const res = await api.patch(`/api/admin/messages/${m.id}`, { status: next });
            setMessages((list) => list.map((x) => (x.id === m.id ? res.data : x)));
            setSelected((s) => (s?.id === m.id ? res.data : s));
            toast.success("Η κατάσταση ενημερώθηκε.");
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    const remove = async (m) => {
        try {
            await api.delete(`/api/admin/messages/${m.id}`);
            toast.success("Το μήνυμα διαγράφηκε.");
            setSelected(null);
            load();
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    return (
        <div className="mx-auto max-w-6xl" data-testid="admin-messages-page">
            <h1 className="text-2xl font-bold text-[#F2F5F6]">Μηνύματα</h1>
            <p className="mt-1 text-sm text-[#697B80]">Υποβολές από τη φόρμα επικοινωνίας και υποστήριξης του site.</p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
                <div className="flex flex-wrap gap-2" data-testid="message-status-filters">
                    {STATUS_FILTERS.map((f) => (
                        <button
                            key={f.id}
                            onClick={() => setStatus(f.id)}
                            className={`rounded-full border px-4 py-1.5 text-xs font-bold transition-colors duration-200 ${
                                status === f.id ? "border-[#22A6C0] bg-[#22A6C0]/15 text-[#4CC9DC]" : "border-white/10 text-[#A3B3B8] hover:border-white/25"
                            }`}
                            data-testid={`msg-filter-${f.id || "all"}`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
                <div className="relative min-w-[200px] flex-1">
                    <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#697B80]" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Αναζήτηση σε όνομα, email ή μήνυμα…"
                        className={`${inputCls} pl-10`}
                        data-testid="message-search"
                    />
                </div>
            </div>

            {messages === null ? (
                <Spinner testId="messages-loading" />
            ) : messages.length === 0 ? (
                <p className="mt-16 text-center text-sm text-[#697B80]" data-testid="messages-empty">Δεν βρέθηκαν μηνύματα.</p>
            ) : (
                <ul className="mt-6 space-y-3" data-testid="messages-list">
                    {messages.map((m) => (
                        <li key={m.id}>
                            <button
                                onClick={() => setSelected(m)}
                                className="w-full rounded-2xl border border-white/10 bg-[#093448] p-4 text-left transition-colors duration-200 hover:border-[#22A6C0]/40"
                                data-testid={`message-row-${m.id}`}
                            >
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <p className="text-sm font-bold text-[#F2F5F6]">
                                        {m.name}
                                        {m.role && <span className="ml-2 text-xs font-normal text-[#697B80]">{m.role}</span>}
                                    </p>
                                    <div className="flex items-center gap-2">
                                        {m.android_interest && (
                                            <span className="rounded-full border border-[#5FBBAC]/40 bg-[#5FBBAC]/10 px-2.5 py-0.5 text-[11px] font-bold text-[#5FBBAC]" data-testid={`message-android-${m.id}`}>
                                                Android
                                            </span>
                                        )}
                                        <span className="rounded-full border border-white/10 px-2.5 py-0.5 text-[11px] font-bold text-[#A3B3B8]">
                                            {m.type === "support" ? "Υποστήριξη" : "Επικοινωνία"}
                                        </span>
                                        <StatusBadge status={m.status} />
                                    </div>
                                </div>
                                <p className="mt-1 text-xs text-[#4CC9DC]">{m.email}</p>
                                <p className="mt-2 line-clamp-2 text-sm text-[#A3B3B8]">{m.subject ? `${m.subject} — ` : ""}{m.message}</p>
                                <p className="mt-2 text-[11px] text-[#697B80]">{fmtDateTime(m.created_at)}</p>
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {/* Detail modal */}
            {selected && (
                <div className="fixed inset-0 z-50 overflow-y-auto bg-black/70 p-4 backdrop-blur-sm" data-testid="message-modal">
                    <div className="mx-auto my-8 max-w-2xl rounded-3xl border border-white/10 bg-[#052536] p-6 md:p-8">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h2 className="text-xl font-bold text-[#F2F5F6]" data-testid="message-modal-name">{selected.name}</h2>
                                <p className="mt-0.5 text-sm text-[#4CC9DC]">{selected.email}</p>
                                {selected.role && <p className="mt-0.5 text-xs text-[#697B80]">{selected.role}</p>}
                            </div>
                            <button onClick={() => setSelected(null)} className="rounded-full border border-white/10 p-2 text-[#A3B3B8] hover:text-white" aria-label="Κλείσιμο" data-testid="message-close">
                                <X size={16} />
                            </button>
                        </div>

                        <div className="mt-4 flex flex-wrap items-center gap-2">
                            <StatusBadge status={selected.status} />
                            <span className="text-[11px] text-[#697B80]">{fmtDateTime(selected.created_at)}</span>
                            <span className="rounded-full border border-white/10 px-2.5 py-0.5 text-[11px] font-bold text-[#A3B3B8]">
                                {selected.type === "support" ? "Υποστήριξη" : "Επικοινωνία"}
                            </span>
                            {selected.android_interest && (
                                <span className="rounded-full border border-[#5FBBAC]/40 bg-[#5FBBAC]/10 px-2.5 py-0.5 text-[11px] font-bold text-[#5FBBAC]" data-testid="message-modal-android">
                                    Ενδιαφέρον για Android
                                </span>
                            )}
                        </div>

                        {selected.subject && (
                            <p className="mt-5 text-sm font-semibold text-[#F2F5F6]" data-testid="message-modal-subject">Θέμα: {selected.subject}</p>
                        )}
                        <p className="mt-3 whitespace-pre-wrap rounded-2xl border border-white/5 bg-[#093448] p-4 text-sm leading-relaxed text-[#F2F5F6]" data-testid="message-modal-body">
                            {selected.message}
                        </p>

                        <div className="mt-6 flex flex-wrap gap-2">
                            <a
                                href={`mailto:${selected.email}?subject=${encodeURIComponent(`Re: TheraOffice — ${selected.subject || "μήνυμά σου"}`)}`}
                                className="inline-flex items-center gap-2 rounded-full bg-[#22A6C0] px-4 py-2 text-xs font-bold text-[#052536] transition-colors hover:bg-[#4CC9DC]"
                                data-testid="message-reply-email"
                            >
                                <Mail size={14} /> Απάντηση με email
                            </a>
                            {selected.status !== "read" && (
                                <button onClick={() => setMsgStatus(selected, "read")} className={btnGhost} data-testid="message-mark-read">
                                    <MailOpen size={14} /> Σήμανση ως διαβασμένο
                                </button>
                            )}
                            {selected.status !== "replied" && (
                                <button onClick={() => setMsgStatus(selected, "replied")} className={btnGhost} data-testid="message-mark-replied">
                                    <MailCheck size={14} /> Σήμανση ως απαντήθηκε
                                </button>
                            )}
                            {selected.status !== "new" && (
                                <button onClick={() => setMsgStatus(selected, "new")} className={btnGhost} data-testid="message-mark-new">
                                    <Undo2 size={14} /> Σήμανση ως νέο
                                </button>
                            )}
                            <ConfirmButton onConfirm={() => remove(selected)} label="Διαγραφή" testId="message-delete" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
